import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { organizationService } from "../services/organizationService";
import type { Organization, AssetNumberingSettings } from "../types/database";

// Settings sections
const SECTIONS = [
  { id: "general", label: "General" },
  { id: "organization", label: "Organization" },
  { id: "assets", label: "Asset Management" },
  { id: "team", label: "Team" },
  { id: "appearance", label: "Appearance" },
];

export default function Settings() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("general");

  if (!user) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-gray-900">Settings</h1>
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          Please log in to access settings.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account preferences and organization settings
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="w-full lg:w-64 flex-shrink-0">
          <nav className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">
              Settings
            </h2>
            <ul className="space-y-1">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors font-medium text-sm
                      ${
                        activeSection === section.id
                          ? "bg-primary/10 text-primary shadow-sm"
                          : "text-gray-700 hover:bg-gray-50"
                      }
                    `}
                    onClick={() => setActiveSection(section.id)}
                  >
                    {section.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {activeSection === "general" && <GeneralSettings />}
          {activeSection === "organization" && <OrganizationSettingsSection />}
          {activeSection === "assets" && <AssetSettings />}
          {activeSection === "team" && <TeamSettings />}
          {activeSection === "appearance" && <AppearanceSettings />}
        </main>
      </div>
    </div>
  );
}

function GeneralSettings() {
  const [defaultCurrency, setDefaultCurrency] = useState("GBP");
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [timeFormat, setTimeFormat] = useState("24h");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [maintenanceReminders, setMaintenanceReminders] = useState(true);
  const [inventoryAlerts, setInventoryAlerts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState("");

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSuccess("Preferences saved successfully!");
    setIsSaving(false);
    setTimeout(() => setSuccess(""), 3000);
  };

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 text-gray-900">
          User Preferences
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Currency
            </label>
            <select
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="GBP">British Pound (£)</option>
              <option value="USD">US Dollar ($)</option>
              <option value="EUR">Euro (€)</option>
              <option value="JPY">Japanese Yen (¥)</option>
              <option value="AUD">Australian Dollar (A$)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Format
            </label>
            <select
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Format
            </label>
            <select
              value={timeFormat}
              onChange={(e) => setTimeFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="24h">24-hour</option>
              <option value="12h">12-hour (AM/PM)</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 text-gray-900">
          Notifications
        </h2>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                Email Notifications
              </h3>
              <p className="text-sm text-gray-500">
                Receive email notifications for important events
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                Maintenance Reminders
              </h3>
              <p className="text-sm text-gray-500">
                Get notified when equipment needs maintenance
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={maintenanceReminders}
                onChange={(e) => setMaintenanceReminders(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                Inventory Alerts
              </h3>
              <p className="text-sm text-gray-500">
                Receive alerts for low inventory or stock issues
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={inventoryAlerts}
                onChange={(e) => setInventoryAlerts(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Notification Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrganizationSettingsSection() {
  const { currentOrganization, user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!currentOrganization) return;
    setLoading(true);
    organizationService
      .getOrganization(currentOrganization.id)
      .then((org) => {
        setOrganization(org);
        setName(org.name);
        setPrimaryColor(org.primary_color || "#3B82F6");
        setSecondaryColor(org.secondary_color || "#1E40AF");
        setLogoPreview(org.logo_url);
      })
      .catch(() => setError("Failed to load organization data"))
      .finally(() => setLoading(false));
  }, [currentOrganization]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogo(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;
    setIsSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await organizationService.updateOrganization(organization.id, {
        name: name.trim(),
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });
      if (logo) {
        await organizationService.uploadLogo(organization.id, logo);
      }
      setSuccess("Organization settings updated successfully");
      const updatedOrg = await organizationService.getOrganization(
        organization.id
      );
      setOrganization(updatedOrg);
    } catch {
      setError("Failed to update organization settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!organization || !user) return;
    if (deleteConfirmation !== organization.name) {
      setError(
        "Please type the organization name correctly to confirm deletion"
      );
      return;
    }

    setIsDeleting(true);
    setError("");
    setSuccess(
      "Deleting organization and all associated data. This may take a moment..."
    );

    try {
      await organizationService.deleteOrganization(organization.id, user.id);
      // Redirect to create organization page
      window.location.href = "/organizations/create";
    } catch (err: any) {
      console.error("Error deleting organization:", err);
      setSuccess("");
      if (err.code === "23503") {
        setError(
          "Unable to delete organization because it still has associated data. Please try again."
        );
      } else {
        setError(err.message || "Failed to delete organization");
      }
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!organization) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-red-500">No organization selected.</div>
      </div>
    );
  }

  // For now, assume user is owner if they can access organization settings
  // In a real implementation, you'd check the user's role in the organization
  const isOwner = true; // TODO: Implement proper role checking

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
          {success}
        </div>
      )}

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 text-gray-900">
          Organization Information
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      {/* Branding */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 text-gray-900">Branding</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Organization Logo
            </label>
            <div className="flex items-start space-x-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt={organization.name}
                      className="h-24 w-24 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <div className="text-center">
                        <svg
                          className="mx-auto h-8 w-8 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="mt-1 text-xs text-gray-500">No logo</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <div className="space-y-3">
                  <div>
                    <input
                      type="file"
                      id="logo"
                      onChange={handleLogoChange}
                      accept="image/*"
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 transition-colors"
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>Recommended: Square image, 256x256 pixels or larger</p>
                    <p>Formats: PNG, JPG, WebP (max 5MB)</p>
                  </div>
                  {logoPreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogo(null);
                        setLogoPreview(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800 transition-colors"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Color Pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Primary Color
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-12 rounded-md border border-gray-300 cursor-pointer shadow-sm hover:border-gray-400 transition-colors"
                  />
                </div>
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary font-mono text-sm"
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            {/* Secondary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Secondary Color
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="h-10 w-12 rounded-md border border-gray-300 cursor-pointer shadow-sm hover:border-gray-400 transition-colors"
                  />
                </div>
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary font-mono text-sm"
                  placeholder="#1E40AF"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Save Branding"}
            </button>
          </div>
        </form>
      </div>

      {/* Danger Zone - Only for owners */}
      {isOwner && (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <h2 className="text-xl font-semibold mb-6 text-red-600">
            Danger Zone
          </h2>

          <div className="border border-red-300 rounded-md p-4">
            <h3 className="text-lg font-medium mb-2 text-red-600">
              Delete Organization
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Once you delete an organization, there is no going back. All data
              associated with this organization will be permanently deleted,
              including inventory items, assets, maintenance records, checkout
              records, and documents.
            </p>

            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors"
              >
                Delete Organization
              </button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="deleteConfirmation"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    To confirm, type{" "}
                    <span className="font-semibold">{organization.name}</span>{" "}
                    below:
                  </label>
                  <input
                    type="text"
                    id="deleteConfirmation"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={organization.name}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleDeleteOrganization}
                    disabled={
                      isDeleting || deleteConfirmation !== organization.name
                    }
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? "Deleting..." : "Delete Organization"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmation("");
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AssetSettings() {
  const { currentOrganization } = useAuth();
  const [assetPrefix, setAssetPrefix] = useState("");
  const [assetSuffix, setAssetSuffix] = useState("");
  const [startingNumber, setStartingNumber] = useState(1000);
  const [paddingLength, setPaddingLength] = useState(0);
  const [previewBarcode, setPreviewBarcode] = useState("KITRA-1000");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    async function loadSettings() {
      if (!currentOrganization?.id) return;
      try {
        setIsLoading(true);
        const settings = await organizationService.getOrganizationSettings(
          currentOrganization.id
        );
        if (settings.asset_numbering) {
          setAssetPrefix(settings.asset_numbering.prefix || "");
          setAssetSuffix(settings.asset_numbering.suffix || "");
          setStartingNumber(settings.asset_numbering.current_number || 1000);
          setPaddingLength(settings.asset_numbering.padding_length || 0);
          updatePreview(
            settings.asset_numbering.prefix || "",
            settings.asset_numbering.current_number || 1000,
            settings.asset_numbering.suffix || "",
            settings.asset_numbering.padding_length || 0
          );
        }
      } catch (err) {
        console.error("Error loading asset settings:", err);
        setError("Failed to load asset settings");
      } finally {
        setIsLoading(false);
      }
    }
    loadSettings();
  }, [currentOrganization]);

  const updatePreview = (
    prefix = assetPrefix,
    number = startingNumber,
    suffix = assetSuffix,
    padding = paddingLength
  ) => {
    const paddedNumber = number.toString().padStart(padding, "0");
    const preview = `${prefix}${paddedNumber}${suffix}`;
    setPreviewBarcode(preview);
  };

  const handleSaveSettings = async () => {
    if (!currentOrganization?.id) return;
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      const settings: AssetNumberingSettings = {
        prefix: assetPrefix,
        suffix: assetSuffix,
        starting_number: startingNumber,
        current_number: startingNumber,
        padding_length: paddingLength,
      };
      await organizationService.updateOrganizationSettings(
        currentOrganization.id,
        { asset_numbering: settings }
      );
      setSuccess("Asset numbering settings saved successfully!");
    } catch (err) {
      console.error("Error saving asset settings:", err);
      setError("Failed to save asset settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-6 text-gray-900">
        Barcode Format Settings
      </h2>
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
          {success}
        </div>
      )}

      <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
        <p className="text-2xl font-mono text-gray-900">{previewBarcode}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prefix
          </label>
          <input
            type="text"
            value={assetPrefix}
            onChange={(e) => {
              setAssetPrefix(e.target.value);
              updatePreview(
                e.target.value,
                startingNumber,
                assetSuffix,
                paddingLength
              );
            }}
            placeholder="e.g., KITRA-"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Suffix
          </label>
          <input
            type="text"
            value={assetSuffix}
            onChange={(e) => {
              setAssetSuffix(e.target.value);
              updatePreview(
                assetPrefix,
                startingNumber,
                e.target.value,
                paddingLength
              );
            }}
            placeholder="e.g., -2024"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Starting Number
          </label>
          <input
            type="number"
            value={startingNumber}
            onChange={(e) => {
              const num = parseInt(e.target.value) || 1000;
              setStartingNumber(num);
              updatePreview(assetPrefix, num, assetSuffix, paddingLength);
            }}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Padding Length
          </label>
          <input
            type="number"
            value={paddingLength}
            onChange={(e) => {
              const num = parseInt(e.target.value) || 0;
              setPaddingLength(num);
              updatePreview(assetPrefix, startingNumber, assetSuffix, num);
            }}
            min="0"
            max="10"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="text-sm text-gray-500 mt-1">
            Number of digits to pad to (e.g., 5 for 00001)
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

function TeamSettings() {
  const { currentOrganization, user } = useAuth();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!currentOrganization) return;
    const loadMembers = async () => {
      setLoading(true);
      try {
        const membersList = await organizationService.getOrganizationMembers(
          currentOrganization.id
        );
        setMembers(membersList);
      } catch (err) {
        console.error("Error loading members:", err);
        setError("Failed to load team members");
      } finally {
        setLoading(false);
      }
    };
    loadMembers();
  }, [currentOrganization]);

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !inviteEmail.trim()) return;
    setIsInviting(true);
    setError("");
    setSuccess("");
    try {
      await organizationService.inviteUser(
        inviteEmail.trim(),
        currentOrganization.id,
        inviteRole
      );
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      const updatedMembers = await organizationService.getOrganizationMembers(
        currentOrganization.id
      );
      setMembers(updatedMembers);
    } catch (err) {
      console.error("Error inviting user:", err);
      setError("Failed to invite user. Make sure the email is registered.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (
    _memberId: string,
    userId: string,
    newRole: "owner" | "admin" | "member"
  ) => {
    if (!currentOrganization) return;
    try {
      await organizationService.updateMemberRole(
        currentOrganization.id,
        userId,
        newRole
      );
      const updatedMembers = await organizationService.getOrganizationMembers(
        currentOrganization.id
      );
      setMembers(updatedMembers);
      setSuccess("Member role updated successfully");
    } catch (err) {
      console.error("Error updating member role:", err);
      setError("Failed to update member role");
    }
  };

  const handleRemoveMember = async (
    _memberId: string,
    userId: string,
    role: string
  ) => {
    if (!currentOrganization || !user) return;
    if (userId === user.id) {
      setError("You cannot remove yourself from the organization");
      return;
    }
    const currentUserMember = members.find((m) => m.user_id === user.id);
    if (currentUserMember?.role === "admin" && role === "owner") {
      setError("Admins cannot remove organization owners");
      return;
    }
    if (!window.confirm("Are you sure you want to remove this member?")) {
      return;
    }
    try {
      await organizationService.removeMember(currentOrganization.id, userId);
      const updatedMembers = await organizationService.getOrganizationMembers(
        currentOrganization.id
      );
      setMembers(updatedMembers);
      setSuccess("Member removed successfully");
    } catch (err) {
      console.error("Error removing member:", err);
      setError("Failed to remove member");
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentUserMember = members.find((m) => m.user_id === user?.id);
  const isOwnerOrAdmin =
    currentUserMember?.role === "owner" || currentUserMember?.role === "admin";

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-6 text-gray-900">Team Members</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
          {success}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              {isOwnerOrAdmin && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {members.map((member) => (
              <tr key={member.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {member.profiles?.avatar_url ? (
                      <img
                        src={member.profiles.avatar_url}
                        alt={member.profiles.full_name || "User"}
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        {(member.profiles?.full_name || "U")
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {member.profiles?.full_name || "Unnamed User"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {member.profiles?.email || "No email"}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {isOwnerOrAdmin &&
                  currentUserMember?.role === "owner" &&
                  member.user_id !== user?.id ? (
                    <select
                      value={member.role}
                      onChange={(e) =>
                        handleRoleChange(
                          member.id,
                          member.user_id,
                          e.target.value as "owner" | "admin" | "member"
                        )
                      }
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member.role === "owner"
                          ? "bg-purple-100 text-purple-800"
                          : member.role === "admin"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {member.role.charAt(0).toUpperCase() +
                        member.role.slice(1)}
                    </span>
                  )}
                </td>
                {isOwnerOrAdmin && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {member.user_id !== user?.id && (
                      <button
                        onClick={() =>
                          handleRemoveMember(
                            member.id,
                            member.user_id,
                            member.role
                          )
                        }
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isOwnerOrAdmin && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-4 text-gray-900">
            Invite New Member
          </h3>
          <form
            onSubmit={handleInviteSubmit}
            className="flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
            <select
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(e.target.value as "admin" | "member")
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={isInviting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isInviting ? "Inviting..." : "Invite"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function AppearanceSettings() {
  const [theme, setTheme] = useState("system");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState("");

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSuccess("Appearance settings saved successfully!");
    setIsSaving(false);
    setTimeout(() => setSuccess(""), 3000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-6 text-gray-900">
        Theme & Display
      </h2>
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Theme
          </label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="system">System Default</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Compact Mode</h3>
            <p className="text-sm text-gray-500">
              Reduce spacing and padding for a more compact layout
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={compactMode}
              onChange={(e) => setCompactMode(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">
              Collapsed Sidebar
            </h3>
            <p className="text-sm text-gray-500">
              Start with the sidebar collapsed by default
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={sidebarCollapsed}
              onChange={(e) => setSidebarCollapsed(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : "Save Appearance Settings"}
        </button>
      </div>
    </div>
  );
}
